import { useEffect, useState } from "react";
import { SingleValue } from "react-select";
import { Guid } from "../../../../api/baseApi";
import { TVChannel } from "../../../../api/tvChannelApi";
import { PriorityStreamPreview } from "./types";

export const useStreamEdit = (
    setPriorityStreams: React.Dispatch<React.SetStateAction<PriorityStreamPreview[]>>,
    tvChannelsList: TVChannel[],
) => {
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [editingStreamId, setEditingStreamId] = useState<Guid | null>(null);
    const [editStreamName, setEditStreamName] = useState<string>("");
    const [isEditStreamNameError, setIsEditStreamNameError] = useState<boolean>(false);
    const [editStartTime, setEditStartTime] = useState<string>("");
    const [editEndTime, setEditEndTime] = useState<string>("");
    const [isEditStartDateError, setIsEditStartDateError] = useState<boolean>(false);
    const [isEditEndDateError, setIsEditEndDateError] = useState<boolean>(false);
    const [isEditStreamPeriodError, setIsEditStreamPeriodError] = useState<boolean>(false);
    const [editSeries, setEditSeries] = useState<string>("");
    const [editSeason, setEditSeason] = useState<string>("");
    const [editEpisode, setEditEpisode] = useState<string>("");
    const [editTitle, setEditTitle] = useState<string>("");
    const [editIabCat, setEditIabCat] = useState<string>("");
    const [editGenre, setEditGenre] = useState<string>("");
    const [editRating, setEditRating] = useState<string>("");
    const [editTvChannel, setEditTvChannel] = useState<SingleValue<TVChannel>>(null);

    useEffect(() => {
        if (editEndTime && editStartTime && new Date(editEndTime) <= new Date(editStartTime)) {
            setIsEditStreamPeriodError(true);
        } else {
            setIsEditStreamPeriodError(false);
        }
    }, [editStartTime, editEndTime]);

    const openEditModal = (stream: PriorityStreamPreview) => {
        setEditingStreamId(stream.id);
        setEditStreamName(stream.programName);
        setEditStartTime(stream.startTime.slice(0, 16));
        setEditEndTime(stream.endTime.slice(0, 16));
        setEditSeries(stream.series);
        setEditSeason(stream.season);
        setEditEpisode(stream.episode);
        setEditTitle(stream.title);
        setEditIabCat(stream.iabCat);
        setEditGenre(stream.genre);
        setEditRating(stream.rating);
        setEditTvChannel(tvChannelsList.find(it => it.id === stream.tvChannelId) || null);
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingStreamId(null);
        setEditStreamName("");
        setIsEditStreamNameError(false);
        setEditStartTime("");
        setEditEndTime("");
        setIsEditStartDateError(false);
        setIsEditEndDateError(false);
        setIsEditStreamPeriodError(false);
        setEditSeries("");
        setEditSeason("");
        setEditEpisode("");
        setEditTitle("");
        setEditIabCat("");
        setEditGenre("");
        setEditRating("");
        setEditTvChannel(null);
    };

    const saveEditStream = () => {
        let isError = false;
        if (editStreamName === "") { setIsEditStreamNameError(true); isError = true; }
        if (editStartTime === "") { setIsEditStartDateError(true); isError = true; }
        if (editEndTime === "") { setIsEditEndDateError(true); isError = true; }
        if (new Date(editEndTime) <= new Date(editStartTime)) { setIsEditStreamPeriodError(true); isError = true; }
        if (isError) return;

        setPriorityStreams(prev => prev.map(it => it.id !== editingStreamId ? it : {
            ...it,
            programName: editStreamName,
            startTime: editStartTime,
            endTime: editEndTime,
            tvChannelId: editTvChannel?.id || it.tvChannelId,
            tvChannelName: editTvChannel?.name || it.tvChannelName,
            series: editSeries,
            season: editSeason,
            episode: editEpisode,
            title: editTitle,
            iabCat: editIabCat,
            genre: editGenre,
            rating: editRating,
        }));
        closeEditModal();
    };

    return {
        showEditModal,
        openEditModal,
        closeEditModal,
        saveEditStream,
        fields: {
            editStreamName, setEditStreamName,
            isEditStreamNameError, setIsEditStreamNameError,
            editStartTime, setEditStartTime,
            editEndTime, setEditEndTime,
            isEditStartDateError, setIsEditStartDateError,
            isEditEndDateError, setIsEditEndDateError,
            isEditStreamPeriodError,
            editSeries, setEditSeries,
            editSeason, setEditSeason,
            editEpisode, setEditEpisode,
            editTitle, setEditTitle,
            editIabCat, setEditIabCat,
            editGenre, setEditGenre,
            editRating, setEditRating,
            editTvChannel,
        },
    };
};

export type StreamEditFields = ReturnType<typeof useStreamEdit>["fields"];
